import React, { useState } from "react";
import { Button, Modal, Tabs } from "antd";
import {
    CircleX,
    DatabaseZap,
    FileOutput,
    FileSpreadsheet,
    FileText,
    MessageSquareText,
    PencilLine,
    Power,
    Radio,
    ScanSearch,
    SkipForward
} from "lucide-react";

import {
    backfillOfferRemarksFromAnalog,
    backfillOrderInputModes,
    backfillTextOrderWorkbooks,
    checkSiteAvailability,
    wakeComputer
} from "../api/adminMaintenanceApi";
import styles from "./AdminMaintenancePage.module.css";
import MaterialDownloadsPanel from "./MaterialRoutingPanel";
import ApiLimitsPanel from "./ApiLimitsPanel";
import LlmUsagePanel from "./LlmUsagePanel";
import CatalogTranslations from "./CatalogTranslations";
import CatalogLlmSettings from "./CatalogLlmSettings";

const RESULT_ITEMS = [
    { key: "scanned", label: "Проверено", icon: ScanSearch },
    { key: "text", label: "Текст", icon: FileText },
    { key: "excel", label: "Excel", icon: FileSpreadsheet },
    { key: "skipped", label: "Пропущено", icon: SkipForward }
];
const WORKBOOK_RESULT_ITEMS = [
    { key: "scanned", label: "Проверено", icon: ScanSearch },
    { key: "converted", label: "Преобразовано", icon: FileSpreadsheet },
    { key: "skippedWithoutRows", label: "Без строк", icon: SkipForward },
    { key: "failed", label: "Ошибки", icon: CircleX }
];
const OFFER_REMARK_RESULT_ITEMS = [
    { key: "scannedDocuments", label: "Проверено документов", icon: ScanSearch },
    { key: "documentsWithAnalogColumn", label: "С колонкой analog", icon: FileSpreadsheet },
    { key: "updatedDocuments", label: "Обновлено документов", icon: FileText },
    { key: "updatedOffers", label: "Обновлено позиций", icon: PencilLine },
    { key: "skippedSameRemark", label: "Уже записано", icon: SkipForward },
    { key: "failedDocuments", label: "Ошибки", icon: CircleX }
];

export default function AdminMaintenancePage() {
    const [inputModeLoading, setInputModeLoading] = useState(false);
    const [inputModeResult, setInputModeResult] = useState(null);
    const [inputModeError, setInputModeError] = useState("");
    const [workbookLoading, setWorkbookLoading] = useState(false);
    const [workbookResult, setWorkbookResult] = useState(null);
    const [workbookError, setWorkbookError] = useState("");
    const [offerRemarkLoading, setOfferRemarkLoading] = useState(false);
    const [offerRemarkResult, setOfferRemarkResult] = useState(null);
    const [offerRemarkError, setOfferRemarkError] = useState("");
    const [wakeLoading, setWakeLoading] = useState(false);
    const [wakeSent, setWakeSent] = useState(false);
    const [wakeError, setWakeError] = useState("");
    const [siteCheckLoading, setSiteCheckLoading] = useState(false);
    const [siteCheckResult, setSiteCheckResult] = useState(null);
    const [siteCheckError, setSiteCheckError] = useState("");
    const operationRunning = inputModeLoading || workbookLoading || offerRemarkLoading || wakeLoading || siteCheckLoading;

    const runBackfill = () => {
        Modal.confirm({
            title: "Определить типы загруженных запросов?",
            content: "Система проверит все запросы и обновит признак input_mode. Операция может занять некоторое время.",
            okText: "Запустить",
            cancelText: "Отмена",
            onOk: async () => {
                setInputModeLoading(true);
                setInputModeError("");
                setInputModeResult(null);

                try {
                    setInputModeResult(await backfillOrderInputModes());
                } catch (requestError) {
                    setInputModeError(requestError.message || "Не удалось обновить типы документов");
                } finally {
                    setInputModeLoading(false);
                }
            }
        });
    };

    const runTextWorkbookBackfill = () => {
        Modal.confirm({
            title: "Преобразовать текстовые запросы в Excel?",
            content: "Система пересоздаст исходные файлы всех запросов с типом «Текст» в едином Excel-формате.",
            okText: "Преобразовать",
            cancelText: "Отмена",
            onOk: async () => {
                setWorkbookLoading(true);
                setWorkbookError("");
                setWorkbookResult(null);

                try {
                    setWorkbookResult(await backfillTextOrderWorkbooks());
                } catch (requestError) {
                    setWorkbookError(requestError.message || "Не удалось преобразовать текстовые запросы");
                } finally {
                    setWorkbookLoading(false);
                }
            }
        });
    };

    const runOfferRemarkBackfill = () => {
        Modal.confirm({
            title: "Добавить аналоги в примечания предложений?",
            content:
                "Система проверит все документы предложений, включая неактивные, и добавит значения из колонки analog, analogue или аналог в примечания соответствующих строк.",
            okText: "Запустить",
            cancelText: "Отмена",
            onOk: async () => {
                setOfferRemarkLoading(true);
                setOfferRemarkError("");
                setOfferRemarkResult(null);

                try {
                    setOfferRemarkResult(await backfillOfferRemarksFromAnalog());
                } catch (requestError) {
                    setOfferRemarkError(requestError.message || "Не удалось добавить аналоги в примечания");
                } finally {
                    setOfferRemarkLoading(false);
                }
            }
        });
    };

    const runWakeComputer = () => {
        Modal.confirm({
            title: "Разбудить компьютер?",
            content: "Система отправит Wake-on-LAN пакет. Компьютер включится, если он подключён к сети и Wake-on-LAN разрешён в BIOS/UEFI.",
            okText: "Отправить пакет",
            cancelText: "Отмена",
            onOk: async () => {
                setWakeLoading(true);
                setWakeError("");
                setWakeSent(false);

                try {
                    await wakeComputer();
                    setWakeSent(true);
                } catch (requestError) {
                    setWakeError(requestError.message || "Не удалось отправить Wake-on-LAN пакет");
                } finally {
                    setWakeLoading(false);
                }
            }
        });
    };

    const runSiteCheck = async () => {
        setSiteCheckLoading(true);
        setSiteCheckResult(null);
        setSiteCheckError("");

        try {
            setSiteCheckResult(await checkSiteAvailability());
        } catch (requestError) {
            setSiteCheckError(requestError.message || "Не удалось проверить доступность сайта");
        } finally {
            setSiteCheckLoading(false);
        }
    };

    // [[Maintenance Tabs]] #obsidian-marker
    return (
        <div className={styles.page}>
            <Tabs defaultActiveKey="llm" destroyOnHidden items={[
                { key: "llm", label: "LLM", children: <><CatalogLlmSettings /><CatalogTranslations /><LlmUsagePanel /></> },
                { key: "api-limits", label: "Лимиты API", children: <ApiLimitsPanel /> },
                { key: "materials", label: "Сбор данных", children: <MaterialDownloadsPanel /> },
                { key: "data", label: "Данные и документы", children: <div className={styles.page}>
            <section className={styles.operation}>
                <div className={styles.operationInfo}>
                    <span className={styles.operationIcon}>
                        <DatabaseZap size={22} />
                    </span>
                    <div>
                        <h2>Типы загруженных запросов</h2>
                        <p>
                            Проверяет набор колонок каждого запроса и проставляет тип
                            <strong> Текст</strong> или <strong>Excel</strong>.
                        </p>
                    </div>
                </div>
                <Button type="primary" loading={inputModeLoading} disabled={operationRunning} onClick={runBackfill}>
                    {inputModeLoading ? "Проверка документов…" : "Проверить и обновить"}
                </Button>
            </section>

            {inputModeError && <div className={styles.error}>{inputModeError}</div>}

            {inputModeResult && (
                <section className={styles.result} aria-live="polite">
                    <h3>Обработка завершена</h3>
                    <div className={styles.metrics}>
                        {RESULT_ITEMS.map(({ key, label, icon: Icon }) => (
                            <div className={styles.metric} key={key}>
                                <Icon size={18} />
                                <span>{label}</span>
                                <strong>{inputModeResult[key] ?? 0}</strong>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section className={styles.operation}>
                <div className={styles.operationInfo}>
                    <span className={`${styles.operationIcon} ${styles.operationIconSuccess}`}>
                        <FileOutput size={22} />
                    </span>
                    <div>
                        <h2>Excel-файлы текстовых запросов</h2>
                        <p>
                            Пересоздаёт исходники запросов с типом <strong>Текст</strong> в едином
                            Excel-формате с колонками row number, clean part number, part number, qty и mfg.
                        </p>
                    </div>
                </div>
                <Button
                    type="primary"
                    loading={workbookLoading}
                    disabled={operationRunning}
                    onClick={runTextWorkbookBackfill}
                >
                    {workbookLoading ? "Преобразование…" : "Преобразовать в Excel"}
                </Button>
            </section>

            {workbookError && <div className={styles.error}>{workbookError}</div>}

            {workbookResult && (
                <section className={styles.result} aria-live="polite">
                    <h3>Преобразование завершено</h3>
                    <div className={styles.metrics}>
                        {WORKBOOK_RESULT_ITEMS.map(({ key, label, icon: Icon }) => (
                            <div className={styles.metric} key={key}>
                                <Icon size={18} />
                                <span>{label}</span>
                                <strong>{workbookResult[key] ?? 0}</strong>
                            </div>
                        ))}
                    </div>
                    {workbookResult.failedOrderIds?.length > 0 && (
                        <p className={styles.failedIds}>
                            ID запросов с ошибкой: {workbookResult.failedOrderIds.join(", ")}
                        </p>
                    )}
                </section>
            )}

            <section className={styles.operation}>
                <div className={styles.operationInfo}>
                    <span className={`${styles.operationIcon} ${styles.operationIconWarning}`}>
                        <MessageSquareText size={22} />
                    </span>
                    <div>
                        <h2>Аналоги в примечаниях предложений</h2>
                        <p>
                            Проверяет все Excel-документы предложений и добавляет значения из колонок
                            <strong> analog</strong>, <strong>analogue</strong> или <strong>аналог</strong> в
                            примечания позиций с тем же номером строки.
                        </p>
                    </div>
                </div>
                <Button
                    type="primary"
                    loading={offerRemarkLoading}
                    disabled={operationRunning}
                    onClick={runOfferRemarkBackfill}
                >
                    {offerRemarkLoading ? "Обновление примечаний…" : "Добавить аналоги"}
                </Button>
            </section>

            {offerRemarkError && <div className={styles.error}>{offerRemarkError}</div>}

            {offerRemarkResult && (
                <section className={styles.result} aria-live="polite">
                    <h3>Обновление завершено</h3>
                    <div className={styles.metrics}>
                        {OFFER_REMARK_RESULT_ITEMS.map(({ key, label, icon: Icon }) => (
                            <div className={styles.metric} key={key}>
                                <Icon size={18} />
                                <span>{label}</span>
                                <strong>{offerRemarkResult[key] ?? 0}</strong>
                            </div>
                        ))}
                    </div>
                    {offerRemarkResult.failedFileOfferIds?.length > 0 && (
                        <p className={styles.failedIds}>
                            ID документов предложений с ошибкой: {offerRemarkResult.failedFileOfferIds.join(", ")}
                        </p>
                    )}
                </section>
            )}

                </div> },
                { key: "services", label: "Сервисы", children: <div className={styles.page}>
            <section className={styles.operation}>
                <div className={styles.operationInfo}>
                    <span className={`${styles.operationIcon} ${styles.operationIconPower}`}>
                        <Power size={22} />
                    </span>
                    <div>
                        <h2>Включение компьютера</h2>
                        <p>
                            Отправляет Wake-on-LAN пакет компьютеру в локальной сети. Сервер должен иметь доступ
                            к broadcast-адресу, а Wake-on-LAN должен быть включён в BIOS/UEFI.
                        </p>
                    </div>
                </div>
                <Button type="primary" loading={wakeLoading} disabled={operationRunning} onClick={runWakeComputer}>
                    {wakeLoading ? "Отправка пакета…" : "Разбудить компьютер"}
                </Button>
            </section>

            {wakeError && <div className={styles.error}>{wakeError}</div>}
            {wakeSent && (
                <div className={styles.success} role="status">
                    Wake-on-LAN пакет отправлен. Компьютеру может потребоваться некоторое время для запуска.
                </div>
            )}

            <section className={styles.operation}>
                <div className={styles.operationInfo}>
                    <span className={styles.operationIcon}>
                        <Radio size={22} />
                    </span>
                    <div>
                        <h2>Проверка работоспособности сайта</h2>
                        <p>Проверяет, отвечает ли веб-приложение, и измеряет время получения ответа.</p>
                    </div>
                </div>
                <Button loading={siteCheckLoading} disabled={operationRunning} onClick={runSiteCheck}>
                    {siteCheckLoading ? "Проверка…" : "Проверить сайт"}
                </Button>
            </section>

            {siteCheckError && <div className={styles.error}>{siteCheckError}</div>}
            {siteCheckResult && (
                <div className={styles.success} role="status">
                    Сайт работает · HTTP {siteCheckResult.status} · {siteCheckResult.responseTimeMs} мс
                </div>
            )}
                </div> }
            ]} />
        </div>
    );
}
